import type { Coordinate } from 'ol/coordinate';
import proj4 from 'proj4';
import { LAMBERT93_DEFINITION } from './projection.js';

export interface MapView {
	zoom: number;
	center: Coordinate;
	rotation: number;
}

// Generous bounds around metropolitan France and Corsica, to reject typos and swapped coordinates
const MIN_LAT = 40;
const MAX_LAT = 52;
const MIN_LON = -7;
const MAX_LON = 11;
const MAX_ZOOM = 28;

const lambert93 = proj4('WGS84', LAMBERT93_DEFINITION);

/** Parses `#map=zoom/lat/lon[/rotationInDegrees]`, returns null if absent or invalid. */
export function parseViewHash(hash: string): MapView | null {
	const match = /^#?map=([^&]+)$/.exec(hash);
	if (match === null) return null;

	const parts = match[1].split('/');
	if (parts.length < 3 || parts.length > 4 || parts.includes('')) return null;

	const [zoom, lat, lon, rotationInDegrees = 0] = parts.map(Number);
	if (![zoom, lat, lon, rotationInDegrees].every(Number.isFinite)) return null;
	if (zoom < 0 || zoom > MAX_ZOOM) return null;
	if (lat < MIN_LAT || lat > MAX_LAT || lon < MIN_LON || lon > MAX_LON) return null;

	return {
		zoom,
		center: lambert93.forward([lon, lat]),
		rotation: (rotationInDegrees * Math.PI) / 180
	};
}

export function formatViewHash({ zoom, center, rotation }: MapView): string {
	const [lon, lat] = lambert93.inverse(center);
	const parts = [round(zoom, 2), round(lat, 5), round(lon, 5)];
	const rotationInDegrees = round((rotation * 180) / Math.PI, 1);
	if (rotationInDegrees !== 0) parts.push(rotationInDegrees);

	return `#map=${parts.join('/')}`;
}

function round(value: number, decimals: number): number {
	const factor = 10 ** decimals;
	return Math.round(value * factor) / factor;
}
